import React, { useState } from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import { makeStyles, useTheme as useMuiTheme } from '@material-ui/core/styles';
import { Box, Collapse, Hidden, IconButton } from '@material-ui/core';
import { useTheme } from '../../context/theme/theme';
import { CustomText, CustomH4, CustomFooterTitle, CustomFooterLink } from './FooterComponent';
import SupportSite from './SupportSite';
import { Container } from '../common';
import { useLanguage } from '../../context/language/language';
import { contactEmail, contactPhone, linkDownload } from '../../utils/constant';
import urlResolver from '../../constant/urlPattern';
import { getImageURL } from '../../utils/getImageURL';
import useResponsive from '../../utils/responsive';
import { trackClickFooterLotusLogo, trackClickFooterMenu } from '../../utils/eventTracking';

const lotusLogo = getImageURL('/logo/lotus-logo-footer.svg');
const qrCodeApp = getImageURL('/images/qrcode-app.svg');
const appleDownload = getImageURL('/images/apple-download.svg');
const googlePlayDownload = getImageURL('/images/google-play-download.svg');
const huaweiDownload = getImageURL('/images/huawei-download.svg');
const ToggleUp = getImageURL('/icon/toggle-open-white.svg');
const ToggleDown = getImageURL('/icon/toggle-close-white.svg');

const useStyles = makeStyles((theme,) => ({
  container: {
    position: 'relative',
    zIndex: 2,
    background: `linear-gradient(
      90deg,
      ${theme.metaColors?.startColor || "#00AEA7"} 0%,
      ${theme.metaColors?.endColor || "#147975"} 100%
    )`,
  },
  textCenter: {
    textAlign: 'center',
  },
  mb20: {
    marginBottom: 20,
  },
  pb32: {
    paddingBottom: 32,

    [theme.breakpoints.only('xs')]: {
      paddingBottom: 0,
    },
  },
  pb10: {
    paddingBottom: 10,
  },
  sitemapContainer: {
    display: 'flex',
    justifyContent: 'center',
    wordWrap: 'nowrap',
  },
  logoIcon: (props) => ({
    width: 98,
    height: 24,

    [theme.breakpoints.up('md')]: {
      width: props.columnAmount <= 3 ? 139 : 98,
      height: props.columnAmount <= 3 ? 30 : 24,
    },
    [theme.breakpoints.up('lg')]: {
      width: 139,
      height: 30,
    },
  }),
  footerBg: {
    width: '100%',
    height: 80,
    marginBottom: -1,

    [theme.breakpoints.only('xs')]: {
      height: 32,
    },

    [theme.breakpoints.only('md')]: {
      height: 56,
    },

    '& svg': {
      width: '100%',
      height: '100%'
    }
  },
  gridLogoItem: {
    maxWidth: 170,
    height: 30,
    padding: 0,

    [theme.breakpoints.only('xs')]: {
      maxWidth: "unset",
      height: 24,
      marginBottom: 16,
    },
  },
  gridItem: {
    maxWidth: 170,
    padding: 0,

    [theme.breakpoints.only('xs')]: {
      maxWidth: "unset",
      paddingBottom: 24,
    },
  },
  downloadApp: {
    width: 81,
    height: 24,

    [theme.breakpoints.only('xs')]: {
      width: 94,
      height: 28,
    },
  }
}));

const TelLink = styled.a(({ theme }) => ({
  ...theme.fonts.header3,
  color: theme.colors.white,
}));

const ExpandIcon = styled.img(({ theme }) => ({
  width: 18,
  height: 18,
}));

const QRApp = styled.img(({ theme }) => ({
  width: 72,
  height: 72,
  alignSelf: 'center',
  borderRadius: 4,

  ...theme.isTablet && {
    alignSelf: 'unset',
  }
}));

const DownloadAppImg = styled.img(({ theme }) => ({
  width: 81,
  height: 24,
  
  ...theme.isMobile && {
    width: 94,
    height: 28,
  }
}));

const FooterBg = ({ gradientStart, gradientStop }) => {
  const classes = useStyles();
  return (
    <div className={classes.footerBg}>
      <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="-590.194 -218.113 1439.97 93.752" fill="none">
        <defs>
          <linearGradient id="paint0_linear_8464_20564" x1="-6.18629e-07" y1="219.501" x2="1440" y2="219.501" gradientUnits="userSpaceOnUse" gradientTransform="matrix(1, 0, 0, 1, -590.196594, -218.258194)">
            <stop stop-color={gradientStart} />
            <stop offset="1" stop-color={gradientStop} />
          </linearGradient>
        </defs>
        <path d="M -590.197 -161.441 C -590.197 -161.441 -228.197 -218.258 125.803 -218.258 C 479.803 -218.258 849.803 -161.441 849.803 -161.441 L 849.803 229.742 L -590.197 229.742 L -590.197 -161.441 Z" fill="url(#paint0_linear_8464_20564)" transform="matrix(1, 0, 0, 1, 0, 1.1368683772161603e-13)" />
      </svg>
    </div>
  );
};

const ContactAndDownloadApp = ({ theme }) => {
  const word = useLanguage();
  const classes = useStyles();
  const { isMobile, isTabletOrMobile } = useResponsive();

  return (
    <>
      <Grid
        item
        xs={isMobile ? 12 : 6}
        md={isMobile ? 12 : 2}
        className={classes.gridItem}
      >
        <CustomFooterTitle theme={theme}>
          {word('Header.DownloadApplication')}
        </CustomFooterTitle>
        <Box display="flex" flexDirection={isTabletOrMobile ? "column" : "row"} gridGap={12}>
          {!isMobile && <QRApp width={72} height={72} src={qrCodeApp} alt="QrCode" />}
          <Box display="flex" flexDirection={isMobile ? "row" : "column"} gridGap={isMobile ? 12 : 8} flexWrap="wrap">
            <a
              id="footer-apple-store"
              data-cta-name-en="Footer"
              data-cta-name-th="ส่วนท้าย"
              data-cta-type="Link"
              href={linkDownload}
              target="_blank"
              className={classes.downloadApp}
              onClick={() => trackClickFooterMenu('download_application', word('Footer.AppleStore'))}
              rel="noreferrer"
            >
              <DownloadAppImg src={appleDownload} alt="AppleDownload" />
            </a>
            <a
              id="footer-google-play"
              data-cta-name-en="Footer"
              data-cta-name-th="ส่วนท้าย"
              data-cta-type="Link"
              href={linkDownload}
              target="_blank"
              className={classes.downloadApp}
              onClick={() => trackClickFooterMenu('download_application', word('Footer.GooglePlay'))}
              rel="noreferrer"
            >
              <DownloadAppImg src={googlePlayDownload} alt="GooglePlayDownload" />
            </a>
            <a
              id="footer-galary"
              data-cta-name-en="Footer"
              data-cta-name-th="ส่วนท้าย"
              data-cta-type="Link"
              href={linkDownload}
              target="_blank"
              className={classes.downloadApp}
              onClick={() => trackClickFooterMenu('download_application', word('Footer.AppGallery'))}
              rel="noreferrer"
            >
              <DownloadAppImg src={huaweiDownload} alt="HuaweiDownload" />
            </a>
          </Box>
        </Box>
      </Grid>
    </>
  );
};

const FooterItemCols = ({ data, theme }) => (
  <>
    {data && data.map(({ columnTitle, columnItems }, listIndex) => (
      <Grid
        item
        xs={6}
        md={2}
        id={`divItemList${listIndex + 1}`}
        key={`colItemList${listIndex + 1}`}
        style={{ maxWidth: 170, padding: 0 }}
      >
        <CustomFooterTitle theme={theme} id={`pItemListHeader${listIndex + 1}`}>
          {columnTitle}
        </CustomFooterTitle>
        <FooterItemListLink
          links={columnItems}
          listIndex={listIndex}
          theme={theme}
        />
      </Grid>
    ))}
    <ContactAndDownloadApp theme={theme} />
  </>
);

const FooterItemColsMobile = ({ data, theme }) => {
  const classes = useStyles();

  const initDataExpanded = data && data.map((x, index) => ({
    index,
    expanded: false,
  }));

  const [expanded, setExpanded] = useState(initDataExpanded);

  const getExpandedByIndex = (index) => {
    const result = expanded.find((exp) => exp.index === index);
    return result.expanded;
  };

  const setExpandedByIndex = (index, result) => {
    const data = expanded.map((exp) => {
      if (exp.index === index) {
        return {
          index: exp.index,
          expanded: result,
        };
      }
      return exp;
    });
    setExpanded(data);
  };

  return (
    <>
      {data && data.map(({ columnTitle, columnItems }, listIndex) => (
        <Grid
          key={`footerItem${listIndex}`}
          item
          xs={12}
          style={{ paddingTop: 0, paddingBottom: listIndex + 1 === data.length ? 24 : 0 }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" pt="14px" pb="14px">
            <CustomText theme={theme} style={{ lineHeight: '20px' }}>{columnTitle}</CustomText>
            <IconButton
              style={{ color: theme.colors.white, padding: 0 }}
              onClick={() => setExpandedByIndex(listIndex, !getExpandedByIndex(listIndex))}
              aria-expanded={expanded}
              aria-label="show more"
            >
              {getExpandedByIndex(listIndex) ? (
                <ExpandIcon src={ToggleUp} alt="ExpandLessIcon" />
              ) : (
                <ExpandIcon src={ToggleDown} alt="ExpandMoreIcon" />
              )}
            </IconButton>
          </Box>
          <Collapse
            in={getExpandedByIndex(listIndex)}
            timeout="auto"
            unmountOnExit
          >
            <FooterItemListLink
              links={columnItems}
              listIndex={listIndex}
              theme={theme}
            />
          </Collapse>
        </Grid>
      ))}
      <Grid item xs={12} md={2} className={classes.gridItem}>
        <CustomFooterTitle theme={theme}>
          Contact us
        </CustomFooterTitle>
        <ul className="footer-item-list">
          <li style={{ marginBottom: 4 }}>
            <CustomFooterLink
              id="footer-mail"
              data-cta-name-en="Footer"
              data-cta-name-th="ส่วนท้าย"
              data-cta-type="Link"
              className="footer-text-wrap"
              theme={theme}
              href={`mailto:${contactEmail}`}
            >
              {contactEmail}
            </CustomFooterLink>
          </li>
          <li style={{ marginBottom: 0 }}>
            <TelLink
              id="footer-tel"
              data-cta-name-en="Footer"
              data-cta-name-th="ส่วนท้าย"
              data-cta-type="Link"
              href={`tel:${contactPhone}`}
              className="footer-text-wrap"
            >
              {contactPhone}
            </TelLink>
          </li>
        </ul>
      </Grid>
      <ContactAndDownloadApp isMobile theme={theme} />
    </>
  );
};

const FooterItemListLink = ({ links, listIndex, theme }) => {
  const { isMobile } = useResponsive();

  return (
    <ul className="footer-item-list" id={`ulItemList${listIndex + 1}`}>
      {links && links.map(({ topic, url, openInNewTab }, itemIndex) => {
        const isLastChild = itemIndex + 1 === links?.length;
        return (
          <li
            key={`liItem${listIndex + 1}_${itemIndex + 1}`}
            style={isMobile
              ? { padding: '4px 0', marginBottom: isLastChild ? 0 : 12 }
              : { marginBottom: isLastChild ? 0 : 16 }}
          >
            <CustomH4
              id={`aItem${listIndex + 1}_${itemIndex + 1}`}
              className="footer-text-wrap"
              theme={theme}
              onClick={() => {
              trackClickFooterMenu('about', topic);
                if (openInNewTab) {
                  window.open(url, '_blank').focus();
                } else {
                  window.location.href = url;
                }
              }}
            >
              {topic}
            </CustomH4>
          </li>
        );
      })}
    </ul>
  ); 
};

const FooterLink = ({ footerlinks }) => {
  const theme = useTheme();
  const { metaColors } = useMuiTheme();
  const classes = useStyles({ columnAmount: footerlinks?.length });
  const href = urlResolver('home');
  const { isMobile } = useResponsive();
  return (
    <div style={{ position: 'relative' }}>
      <FooterBg gradientStart={metaColors?.startColor || "#00AEA7"} gradientStop={metaColors?.endColor || "#147975"} />
      <div className={classes.container}>
        <Container customStyle={{ marginBottom: 0 }}>
          <Grid
            container
            justifyContent="space-between"
            className={classes.pb32}
          >
            <Grid item xs={12} md={2} className={classes.gridLogoItem}>
              <a
                id="footer-home"
                data-cta-name-en="Footer"
                data-cta-name-th="ส่วนท้าย"
                data-cta-type="Link"
                href={href}
                onClick={trackClickFooterLotusLogo}
                style={{ display: 'inline-block', height: isMobile ? 24 : 30 }}
              >
                <img width={139} height={30} className={classes.logoIcon} src={lotusLogo} alt="Lotus" />
              </a>
            </Grid>
            <Hidden xsDown>
              <FooterItemCols theme={theme} data={footerlinks} />
            </Hidden>
            <Hidden smUp>
              <FooterItemColsMobile theme={theme} data={footerlinks} />
            </Hidden>
          </Grid>
          <SupportSite />
        </Container>
      </div>
    </div>
  );
};

export default FooterLink;
